import { useEffect, useRef, useState, useCallback } from "react";
import axios from "../../../config/axios";
import useBeaconControl from "../../../hooks/useBeaconControl";

// ---- Alarm sound (loops until stopped) ----
const ALARM_SRC = "/static/timer-alarm.mp3";
const ALARM_VOLUME = 0.7;
let alarmAudio = null;
const startAlarm = () => {
  try {
    if (!alarmAudio) { alarmAudio = new Audio(ALARM_SRC); alarmAudio.loop = true; }
    alarmAudio.volume = ALARM_VOLUME;
    alarmAudio.currentTime = 0;
    alarmAudio.play().catch((e) => console.log("[TimerTV] alarm blocked:", e?.name));
  } catch (e) { console.log("[TimerTV] alarm failed:", e); }
};
const stopAlarm = () => {
  try { if (alarmAudio) { alarmAudio.pause(); alarmAudio.currentTime = 0; } } catch {}
};

// ---- Presets (seconds) ----
const PRESETS = [
  { label: "30 SEC", secs: 30 },
  { label: "1 MIN",  secs: 60 },
  { label: "5 MIN",  secs: 300 },
  { label: "10 MIN", secs: 600 },
  { label: "30 MIN", secs: 1800 },
  { label: "60 MIN", secs: 3600 },
];
const MIN_SECS = 30;

// ---- Band schedules — byte-identical to the display timer. ----
const BAND_EVENTS_FULL = [
  { at: -49,   fx: "CANCEL" },
  { at: -47,   fx: "WAKE_UP_SLOW", vib: "OFF" },
  { at: -42,   fx: "CANCEL" },
  { at: -40,   fx: "WAKE_UP_FAST", vib: "OFF" },
  { at: -35.5, fx: "WAKE_UP_FAST", vib: "OFF" },
  { at: -31,   fx: "WAKE_UP_FAST", vib: "OFF" },
  { at: -26.5, fx: "WAKE_UP_FAST", vib: "OFF" },
  { at: -22,   fx: "WAKE_UP_FAST", vib: "OFF" },
  { at: -17.5, fx: "CANCEL" },
  { at: -15.5, fx: "CIRCLE_RED", vib: "OFF" },
  { at: -12.5, fx: "CIRCLE_RED", vib: "OFF" },
  { at: -8.5,  fx: "CANCEL" },
  { at: -6.5,  fx: "WAKE_UP_FAST", vib: "OFF" },
  { at: -3.5,  fx: "CANCEL" },
  { at: -1.5,  fx: "LIGHTNING_RED", vib: "PULSE" },
  { at: 1.5,   fx: "LIGHTNING_RED", vib: "PULSE" },
];
const BAND_EVENTS_COMPACT = [
  { at: -27,   fx: "CANCEL" },
  { at: -25,   fx: "WAKE_UP_SLOW", vib: "OFF" },
  { at: -21,   fx: "CANCEL" },
  { at: -19.5, fx: "WAKE_UP_FAST", vib: "OFF" },
  { at: -17.5, fx: "CANCEL" },
  { at: -15.5, fx: "CIRCLE_RED", vib: "OFF" },
  { at: -12.5, fx: "CIRCLE_RED", vib: "OFF" },
  { at: -8.5,  fx: "CANCEL" },
  { at: -6.5,  fx: "WAKE_UP_FAST", vib: "OFF" },
  { at: -3.5,  fx: "CANCEL" },
  { at: -1.5,  fx: "LIGHTNING_RED", vib: "PULSE" },
  { at: 1.5,   fx: "LIGHTNING_RED", vib: "PULSE" },
];
const FULL_SCHEDULE_MIN_SECS = 55;
const ALARM_REPEAT_MS = 5000;
const ALARM_MAX_MS = 60000;
const GAP_MS = 2000;

/* ---- Raw transport — same endpoints as InteractTV ---- */
const postShowCmd = (deviceId, fx, vib) =>
  axios.post("/esp/beacon-show", { deviceId, fx, vib }, { withCredentials: true })
    .catch((err) => console.error("[TimerTV] cmd failed:", fx, err));
const postCancelRaw = (deviceId) =>
  axios.post("/esp/cancel", { deviceId }, { withCredentials: true })
    .catch((err) => console.error("[TimerTV] cancel failed:", err));

const pad2 = (n) => String(n).padStart(2, "0");
const fmt = (secs) => {
  const s = Math.max(0, Math.ceil(secs));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(r)}` : `${pad2(m)}:${pad2(r)}`;
};

// ---- Remote keys (BeaconDemo-proven set) ----
const OKK = (e) => e.key === "Enter" || e.key === " " || e.key === "OK" || e.key === "Select" ||
  e.keyCode === 13 || e.keyCode === 23 || e.keyCode === 66;
const BACKK = (e) => e.key === "Escape" || e.keyCode === 27 || e.keyCode === 461 || e.keyCode === 10009;

// ---- Focus grid: rows top→bottom, cols left→right. Action row is bottom. ----
const NAV_ROWS = [
  ["p0", "p1", "p2"],
  ["p3", "p4", "p5"],
  ["hplus", "mplus", "splus"],
  ["hminus", "mminus", "sminus"],
  ["back", "primary"],
];
const NAV_POS = {};
NAV_ROWS.forEach((row, r) => row.forEach((k, c) => { NAV_POS[k] = [r, c]; }));

const IXTT_STYLE = `
  .mbtv-strips{position:relative;height:0;z-index:500;overflow:visible;}
  .mbtv-strips .mbtv-strip{box-shadow:0 4px 14px rgba(20,30,60,.25);}
  .ixtt-timer{display:grid;grid-template-columns:1.05fr 0.95fr;gap:28px;width:100%;height:100%;
    min-height:0;font-family:"Lato",sans-serif;color:#1a2542;position:relative;outline:none;}
  .ixtt-panel{background:#fff;border-radius:28px;padding:26px 32px;display:flex;
    flex-direction:column;box-shadow:0 10px 34px rgba(20,30,60,.08);min-height:0;overflow:hidden;}
  .ixtt-focusable:focus{outline:none;}
  .ixtt-tag{align-self:flex-start;background:#007bff;color:#fff;font-size:22px;
    font-weight:900;letter-spacing:3px;padding:8px 18px;border-radius:8px;margin-bottom:14px;}
  .ixtt-title{font-size:38px;font-weight:900;color:#1a2542;margin:0 0 4px;
    letter-spacing:-.5px;line-height:1.05;}
  .ixtt-tagline{font-size:21px;font-weight:800;color:#007bff;margin:0 0 14px;}
  .ixtt-presets{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:12px;}
  .ixtt-preset{height:62px;border-radius:18px;border:4px solid #007bff;background:#fff;
    font-family:"Lato",sans-serif;font-size:23px;font-weight:900;letter-spacing:1px;color:#1a2542;
    cursor:pointer;outline:none;display:flex;align-items:center;justify-content:center;
    transition:transform .12s ease, box-shadow .12s ease, background .12s ease, color .12s ease;}
  .ixtt-preset.on{background:#007bff;color:#fff;}
  .ixtt-preset:focus{background:#007bff;color:#fff;border-color:#05f651;
    box-shadow:0 0 0 5px #05f651,0 0 30px rgba(5,246,81,.5);transform:scale(1.03);
    z-index:1;position:relative;}
  .ixtt-custom{background:#f1f3f7;border:none;border-radius:18px;
    padding:12px 16px 10px;margin-bottom:12px;}
  .ixtt-custom-label{font-size:15px;font-weight:900;letter-spacing:3px;color:#46506b;
    text-align:center;margin-bottom:6px;}
  .ixtt-manual{display:flex;gap:22px;justify-content:center;align-items:flex-start;}
  .ixtt-unit{display:flex;flex-direction:column;align-items:center;gap:6px;}
  .ixtt-unit-label{font-size:15px;font-weight:900;letter-spacing:2px;color:#46506b;}
  .ixtt-step{width:80px;height:50px;border-radius:16px;border:4px solid #d8dce3;background:#fff;
    font-family:"Lato",sans-serif;font-size:28px;font-weight:900;color:#1a2542;cursor:pointer;
    outline:none;line-height:1;transition:transform .12s ease, box-shadow .12s ease, border-color .12s ease;}
  .ixtt-step:focus{border-color:#05f651;box-shadow:0 0 0 5px #05f651,0 0 30px rgba(5,246,81,.5);
    transform:scale(1.05);z-index:1;position:relative;}
  .ixtt-unit-val{font-size:34px;font-weight:900;min-width:70px;text-align:center;
    font-variant-numeric:tabular-nums;}
  .ixtt-actions{margin-top:auto;display:flex;gap:16px;}
  .ixtt-btn{height:70px;border-radius:16px;border:4px solid transparent;font-family:"Lato",sans-serif;
    font-size:27px;font-weight:900;letter-spacing:1px;cursor:pointer;outline:none;color:#fff;
    display:flex;align-items:center;justify-content:center;
    transition:transform .12s ease, box-shadow .12s ease, background .12s ease, color .12s ease;}
  .ixtt-back{flex:0 0 210px;background:#fff;color:#1a2542;border-color:#d8dce3;}
  .ixtt-back:focus{border-color:#05f651;box-shadow:0 0 0 5px #05f651,0 0 30px rgba(5,246,81,.5);
    transform:scale(1.03);}
  .ixtt-start{flex:1;background:#007bff;text-transform:uppercase;letter-spacing:2px;}
  .ixtt-start:focus{background:#0066d6;border-color:#05f651;
    box-shadow:0 0 0 5px #05f651,0 0 30px rgba(5,246,81,.5);transform:scale(1.03);}
  .ixtt-start:disabled{background:#b9c0cd;cursor:default;box-shadow:none;}
  .ixtt-stop{flex:1;background:#e5484d;text-transform:uppercase;letter-spacing:2px;}
  .ixtt-stop:focus{background:#c73737;border-color:#05f651;
    box-shadow:0 0 0 5px #05f651,0 0 30px rgba(5,246,81,.5);transform:scale(1.03);}
  .ixtt-note{margin-top:8px;font-size:15px;color:#8a93a6;text-align:center;}
  .ixtt-clockpanel{align-items:center;justify-content:center;gap:14px;transition:background .3s ease;}
  .ixtt-ringwrap{position:relative;width:360px;height:360px;display:flex;
    align-items:center;justify-content:center;}
  .ixtt-ring-svg{position:absolute;inset:0;transform:rotate(-90deg);}
  .ixtt-ring-track{fill:none;stroke:#eef1f7;stroke-width:16;}
  .ixtt-ring-bar{fill:none;stroke-width:16;stroke-linecap:round;
    transition:stroke-dashoffset .25s linear, stroke .4s ease;}
  .ixtt-clock{font-weight:900;letter-spacing:1px;color:#1a2542;line-height:1;
    font-variant-numeric:tabular-nums;transition:color .4s ease;position:relative;z-index:1;}
  .ixtt-clock.amber{color:#ff9f0a;text-shadow:0 0 22px rgba(255,159,10,.4);}
  .ixtt-clock.red{color:#ff453a;text-shadow:0 0 26px rgba(255,69,58,.5);
    animation:ixtt-pulse 1s ease-in-out infinite;}
  .ixtt-sub{font-size:24px;font-weight:900;color:#8a93a6;letter-spacing:2px;}
  .ixtt-timer.ixtt-alarming .ixtt-clockpanel{animation:ixtt-alarm-bg .8s linear infinite;}
  .ixtt-timer.ixtt-alarming .ixtt-clock{color:#fff;animation:none;text-shadow:none;}
  .ixtt-timer.ixtt-alarming .ixtt-sub{color:#fff;}
  .ixtt-timer.ixtt-alarming .ixtt-ring-track,
  .ixtt-timer.ixtt-alarming .ixtt-ring-bar{stroke:rgba(255,255,255,.35);}
  @keyframes ixtt-pulse{0%,100%{transform:scale(1);}50%{transform:scale(1.05);}}
  @keyframes ixtt-alarm-bg{0%,49%{background:#e5484d;}50%,100%{background:#a61b20;}}
  .ixtt-modal-back{position:fixed;inset:0;background:rgba(0,0,0,.62);z-index:10000;
    display:flex;align-items:center;justify-content:center;}
  .ixtt-modal{background:#fff;border-radius:20px;padding:44px 40px 36px;max-width:720px;
    width:calc(100% - 80px);text-align:center;font-family:"Lato",sans-serif;
    box-shadow:0 16px 50px rgba(0,0,0,.5);}
  .ixtt-modal h3{margin:0 0 16px;font-size:40px;color:#1f2937;font-weight:900;}
  .ixtt-modal p{margin:0 0 32px;font-size:26px;color:#4b5563;line-height:1.4;}
  .ixtt-modal-btns{display:flex;gap:18px;justify-content:center;}
  .ixtt-modal-btn{flex:1;padding:20px 28px;border-radius:14px;border:4px solid transparent;
    font-family:"Lato",sans-serif;font-size:28px;font-weight:900;cursor:pointer;outline:none;
    transition:transform .12s ease, box-shadow .12s ease;}
  .ixtt-modal-btn:focus{border-color:#05f651;box-shadow:0 0 0 3px rgba(5,246,81,.4);transform:scale(1.06);}
  .ixtt-modal-no{background:#e5e7eb;color:#374151;border-color:#9ca3af;}
  .ixtt-modal-yes{background:#007bff;color:#fff;}
`;

const RING_R = 150;
const RING_C = 2 * Math.PI * RING_R;

const InteractTimerTV = ({ onBack, onExitTop, device }) => {
  const [duration, setDuration] = useState(300);
  const [preset, setPreset] = useState(300);
  const [manH, setManH] = useState(0);
  const [manM, setManM] = useState(5);
  const [manS, setManS] = useState(0);
  const [running, setRunning] = useState(false);
  const [alarm, setAlarm] = useState(false);
  const [remaining, setRemaining] = useState(300);

  /* ---- Beacon control + gated senders ---- */
  const { isController, hasHolder, holder, claim } = useBeaconControl();
  const [pendingControl, setPendingControl] = useState(false);
  const isControllerRef = useRef(isController);
  useEffect(() => { isControllerRef.current = isController; }, [isController]);
  const deviceId = device?.magic_beacon_device?._id ?? null;
  const deviceIdRef = useRef(deviceId);
  useEffect(() => { deviceIdRef.current = deviceId; }, [deviceId]);
  const lastCmdAtRef = useRef(0);
  const sentAnyRef = useRef(false);
  const canSend = useCallback(() => !!deviceIdRef.current && !!isControllerRef.current, []);
  const sendCmd = useCallback((fx, vib) => {
    if (!canSend()) { console.log(`[TimerTV] SKIPPED fx=${fx}`); return Promise.resolve(); }
    console.log(`[TimerTV] send fx=${fx} vib=${vib}`);
    sentAnyRef.current = true;
    lastCmdAtRef.current = Date.now();
    return postShowCmd(deviceIdRef.current, fx, vib);
  }, [canSend]);
  const sendCancel = useCallback(() => {
    if (!canSend()) { console.log("[TimerTV] SKIPPED CANCEL"); return Promise.resolve(); }
    console.log("[TimerTV] send CANCEL");
    sentAnyRef.current = true;
    lastCmdAtRef.current = Date.now();
    return postCancelRaw(deviceIdRef.current);
  }, [canSend]);

  /* ---- Engine (identical to display) ---- */
  const endAtRef = useRef(0);
  const tickRef = useRef(null);
  const firedRef = useRef(new Set());
  const scheduleRef = useRef(BAND_EVENTS_FULL);
  const stopTimersRef = useRef([]);
  const alarmRepeatRef = useRef(null);
  const stopRef = useRef(null);

  const clearEngine = useCallback(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (alarmRepeatRef.current) { clearInterval(alarmRepeatRef.current); alarmRepeatRef.current = null; }
    stopTimersRef.current.forEach(clearTimeout);
    stopTimersRef.current = [];
  }, []);

  const tick = useCallback(() => {
    const remainSecs = (endAtRef.current - Date.now()) / 1000;
    setRemaining(remainSecs);
    scheduleRef.current.forEach((ev, i) => {
      if (firedRef.current.has(i)) return;
      if (remainSecs <= -ev.at) {
        firedRef.current.add(i);
        ev.fx === "CANCEL" ? sendCancel() : sendCmd(ev.fx, ev.vib);
      }
    });
    if (remainSecs <= 0 && !firedRef.current.has("alarm")) {
      firedRef.current.add("alarm");
      setAlarm(true);
      startAlarm();
      alarmRepeatRef.current = setInterval(() => {
        if (Date.now() - endAtRef.current >= ALARM_MAX_MS) {
          stopRef.current?.();
          return;
        }
        sendCmd("LIGHTNING_RED", "PULSE");
      }, ALARM_REPEAT_MS);
    }
  }, [sendCancel, sendCmd]);

  const manualSecs = manH * 3600 + manM * 60 + manS;
  const chosenSecs = preset != null ? preset : manualSecs;

  const start = () => {
    if (running) return;
    const secs = Math.max(MIN_SECS, chosenSecs);
    setDuration(secs);
    setRemaining(secs);
    firedRef.current = new Set();
    scheduleRef.current = secs >= FULL_SCHEDULE_MIN_SECS ? BAND_EVENTS_FULL : BAND_EVENTS_COMPACT;
    endAtRef.current = Date.now() + secs * 1000;
    setRunning(true);
    setAlarm(false);
    tickRef.current = setInterval(tick, 250);
  };

  const requestStart = async () => {
    if (running) return;
    if (isController) { start(); return; }
    if (hasHolder && holder?.holderDeviceId !== device?._id) {
      setPendingControl(true);
      return;
    }
    const r = await claim();
    if (r?.ok) start();
    else { console.error("[TimerTV] claim failed:", r?.reason); start(); }
  };
  const confirmTakeControl = async () => {
    setPendingControl(false);
    const r = await claim();
    if (!r?.ok) console.error("[TimerTV] take-control claim failed:", r?.reason);
    start();
  };
  const cancelTakeControl = () => { setPendingControl(false); start(); };

  const stop = useCallback(() => {
    stopAlarm();
    clearEngine();
    setRunning(false);
    setAlarm(false);
    if (sentAnyRef.current) {
      sentAnyRef.current = false;
      const wait = Math.max(0, lastCmdAtRef.current + GAP_MS - Date.now());
      const t1 = setTimeout(() => {
        sendCancel();
        const t2 = setTimeout(() => sendCmd("SOLID_OFF", "OFF"), GAP_MS);
        stopTimersRef.current.push(t2);
      }, wait);
      stopTimersRef.current.push(t1);
    }
    setRemaining(Math.max(MIN_SECS, chosenSecs));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearEngine, sendCancel, sendCmd, chosenSecs]);
  useEffect(() => { stopRef.current = stop; }, [stop]);

  useEffect(() => () => {
    stopAlarm();
    clearEngine();
    if (sentAnyRef.current) sendCancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const belowMin = preset == null && manualSecs < MIN_SECS;
  const inLast30 = running && !alarm && remaining <= 30;
  const inLast10 = running && !alarm && remaining <= 10;

  const bumpUnit = (u, dir) => {
    setPreset(null);
    u.set((v) => {
      let n = v + dir;
      if (n < 0) n = u.max;
      if (n > u.max) n = 0;
      if (u.label === "SEC" && dir > 0 && manH === 0 && manM === 0 && n < 30) n = 30;
      return n;
    });
  };

  const pickPreset = (secs) => {
    if (running) return;
    setPreset(secs);
    setRemaining(secs);
    setManH(Math.floor(secs / 3600));
    setManM(Math.floor((secs % 3600) / 60));
    setManS(secs % 60);
  };

  /* ---- TV: focus grid ---- */
  const btnRefs = useRef({});
  const setBtnRef = (key) => (el) => { btnRefs.current[key] = el; };
  const pcNoRef = useRef(null);
  const pcYesRef = useRef(null);

  const focusKey = (key) => btnRefs.current[key]?.focus({ preventScroll: true });

  // Entry / running-state changes: land on the primary action (Start/Stop).
  useEffect(() => {
    const t = setTimeout(() => focusKey("primary"), 60);
    return () => clearTimeout(t);
  }, [running, alarm]);

  useEffect(() => {
    if (pendingControl) {
      const t = setTimeout(() => pcYesRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [pendingControl]);

  const keyOfTarget = (el) => {
    for (const [k, v] of Object.entries(btnRefs.current)) if (v === el) return k;
    return null;
  };

  const handleKey = (e) => {
    e.stopPropagation();
    if (BACKK(e)) { e.preventDefault(); onBack(); return; } // unmount cleanup winds bands down
    if (alarm && OKK(e)) { e.preventDefault(); stop(); return; } // OK anywhere kills the alarm

    const key = keyOfTarget(e.target);
    if (OKK(e)) { e.preventDefault(); e.target?.click?.(); return; }

    if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) return;
    if (!key) { e.preventDefault(); focusKey("primary"); return; }
    const [r, c] = NAV_POS[key];

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      if (c > 0) focusKey(NAV_ROWS[r][c - 1]);
      return;
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      if (c < NAV_ROWS[r].length - 1) focusKey(NAV_ROWS[r][c + 1]);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (r === 0 || (running && r === 4)) { onExitTop?.(); return; } // top edge → tab row
      const up = NAV_ROWS[r - 1];
      focusKey(up[Math.min(c, up.length - 1)]);
      return;
    }
    if (e.key === "ArrowDown") {
      if (r === 4) return; // bottom edge → bottom bar (native drop, wizard convention)
      e.preventDefault();
      const dn = NAV_ROWS[r + 1];
      focusKey(dn[Math.min(c, dn.length - 1)]);
      return;
    }
  };

  const handlePcModalKey = (e) => {
    e.stopPropagation();
    if (e.key === "ArrowLeft") { e.preventDefault(); pcNoRef.current?.focus(); return; }
    if (e.key === "ArrowRight") { e.preventDefault(); pcYesRef.current?.focus(); return; }
    if (e.key === "ArrowUp" || e.key === "ArrowDown") { e.preventDefault(); return; }
    if (OKK(e)) {
      e.preventDefault();
      e.target === pcNoRef.current ? cancelTakeControl() : confirmTakeControl();
      return;
    }
    if (BACKK(e)) { e.preventDefault(); cancelTakeControl(); return; }
  };

  const handleBlur = (e) => {
    if (e.relatedTarget) return; // deliberate move (tabs / bottom bar) — let it go
    setTimeout(() => {
      if (document.activeElement === document.body) focusKey("primary");
    }, 0);
  };

  const clockStr = alarm ? "0:00" : fmt(running ? remaining : Math.max(MIN_SECS, chosenSecs));
  const clockSize = clockStr.length > 5 ? 60 : 78;
  const frac = running && duration > 0 ? Math.max(0, Math.min(1, remaining / duration)) : 1;
  const ringStroke = inLast10 ? "#ff453a" : inLast30 ? "#ff9f0a" : "url(#ixtt-ring-grad)";

  const UNITS = [
    { label: "HOURS", val: manH, set: setManH, max: 9,  plus: "hplus", minus: "hminus" },
    { label: "MIN",   val: manM, set: setManM, max: 59, plus: "mplus", minus: "mminus" },
    { label: "SEC",   val: manS, set: setManS, max: 59, plus: "splus", minus: "sminus" },
  ];

  return (
    <div className={`ixtt-timer ${alarm ? "ixtt-alarming" : ""}`} onKeyDown={handleKey} onBlur={handleBlur}>
      <style>{IXTT_STYLE}</style>

      {/* Left: setup */}
      <div className="ixtt-panel">
      <div className="ixtt-tag">TIMER</div>
        <h1 className="ixtt-title">Set A Timer</h1>
        <div className="ixtt-tagline">Your bands will flash &amp; buzz when time is up!</div>
        <div className="ixtt-presets">
          {PRESETS.map((p, i) => (
            <button
              key={p.secs}
              ref={setBtnRef(`p${i}`)}
              tabIndex={running ? -1 : 0}
              className={`ixtt-preset ixtt-focusable ${preset === p.secs ? "on" : ""}`}
              onClick={() => pickPreset(p.secs)}
              disabled={running}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="ixtt-custom">
          <div className="ixtt-custom-label">CUSTOM</div>
          <div className="ixtt-manual">
            {UNITS.map((u) => (
              <div className="ixtt-unit" key={u.label}>
                <button ref={setBtnRef(u.plus)} tabIndex={running ? -1 : 0} className="ixtt-step ixtt-focusable" disabled={running} onClick={() => bumpUnit(u, 1)}>+</button>
                <div className="ixtt-unit-val">{pad2(u.val)}</div>
                <button ref={setBtnRef(u.minus)} tabIndex={running ? -1 : 0} className="ixtt-step ixtt-focusable" disabled={running} onClick={() => bumpUnit(u, -1)}>−</button>
                <div className="ixtt-unit-label">{u.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="ixtt-actions">
          <button ref={setBtnRef("back")} tabIndex={0} className="ixtt-btn ixtt-back ixtt-focusable" onClick={onBack}>Back</button>
          {running || alarm ? (
            <button ref={setBtnRef("primary")} tabIndex={0} className="ixtt-btn ixtt-stop ixtt-focusable" onClick={stop}>{alarm ? "Stop Alarm" : "Stop"}</button>
          ) : (
            <button ref={setBtnRef("primary")} tabIndex={0} className="ixtt-btn ixtt-start ixtt-focusable" onClick={requestStart} disabled={belowMin}>Start</button>
          )}
        </div>
        <div className="ixtt-note">
          {belowMin ? "30 second minimum." : "For fun only. Don't rely on the timer or bands for anything time-critical."}
        </div>
      </div>

      {/* Right: countdown */}
      <div className="ixtt-panel ixtt-clockpanel">
        <div className="ixtt-ringwrap">
          <svg className="ixtt-ring-svg" viewBox="0 0 340 340">
            <defs>
              <linearGradient id="ixtt-ring-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#007bff" />
                <stop offset="100%" stopColor="#64d2ff" />
              </linearGradient>
            </defs>
            <circle className="ixtt-ring-track" cx="170" cy="170" r={RING_R} />
            <circle
              className="ixtt-ring-bar"
              cx="170" cy="170" r={RING_R}
              stroke={ringStroke}
              strokeDasharray={RING_C}
              strokeDashoffset={RING_C * (1 - frac)}
            />
          </svg>
          <div
            className={`ixtt-clock ${alarm ? "" : inLast10 ? "red" : inLast30 ? "amber" : ""}`}
            style={{ fontSize: clockSize }}
          >
            {clockStr}
          </div>
        </div>
        <div className="ixtt-sub">
          {alarm ? "TIME'S UP — PRESS OK TO STOP" : running ? "COUNTING DOWN" : "READY"}
        </div>
      </div>

      {pendingControl && hasHolder && holder?.holderDeviceId !== device?._id && (
        <div className="ixtt-modal-back" onKeyDown={handlePcModalKey}>
          <div className="ixtt-modal">
            <h3>Take Control of Your Magic Beacon?</h3>
            <p>
              Your Wait Time {holder.holderDeviceType}: {holder.holderLabel} is
              currently controlling the beacon. The Timer needs control to
              light your bands. Switch control to this device?
            </p>
            <div className="ixtt-modal-btns">
              <button className="ixtt-modal-btn ixtt-modal-no" ref={pcNoRef} tabIndex={0} onClick={cancelTakeControl}>
                No
              </button>
              <button className="ixtt-modal-btn ixtt-modal-yes" ref={pcYesRef} tabIndex={0} onClick={confirmTakeControl}>
                Yes, Switch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractTimerTV;
